import clsx from 'clsx';
import * as React from 'react';
import type { CommissionStatementModuleMode, CommissionStatementModuleName } from '../../../MedicareProAPI/Model/CommissionStatement/CommissionStatementModule';

export type MatchBadgeProps = {
  matched: boolean;
};

const MatchBadge = (props: MatchBadgeProps): React.JSX.Element => {
  const classNames = [
    'badge',
    'small',
    'font-weight-normal',
    'text-white',
    props.matched ? 'bg-success' : 'bg-danger',
  ];
  return <span className={classNames.join(' ')}>{props.matched ? 'Matched' : 'Not Matched'}</span>;
};

export type CommissionStatementResultDataCriteria = Array<string>;

export type CommissionStatementResultDataSummaryEntity = {
  matched: boolean;
  matchedMultiple?: boolean;
  criteria: CommissionStatementResultDataCriteria;
  data: Record<string, string>;
};

export type CommissionStatementResultDataMutateEntity = 'ind' | 'ind_policy' | 'ind_comp_history';

export type CommissionStatementResultDataMutate = {
  entity: CommissionStatementResultDataMutateEntity;
  message: string;
};

export type CommissionStatementResultDataSummary = {
  row: number;
  isDuplicate: boolean;
  agent: CommissionStatementResultDataSummaryEntity;
  ind: CommissionStatementResultDataSummaryEntity;
  plan: CommissionStatementResultDataSummaryEntity;
  policy: CommissionStatementResultDataSummaryEntity;
  compHistory: any; // Improve this
};

export type CommissionStatementResultData = {
  summary: Array<CommissionStatementResultDataSummary>;
  inserted: Array<CommissionStatementResultDataMutate>;
};

export type CommissionStatementProcessingResultProps = {
  data: CommissionStatementResultData;
  module: CommissionStatementModuleName;
  mode?: CommissionStatementModuleMode;
};

export type ToggleCardProps = {
  title: React.JSX.Element;
  children: React.JSX.Element | React.JSX.Element[];
  className?: string;
}

export const ToggleCard = ({ title, children, className }: ToggleCardProps) => {
  const [open, setOpen] = React.useState<boolean>(false);

  const headerClassList = "card-title font-weight-bold mt-2" + (!open ? " mb-0" : "");
  const bodyClassList = clsx(className, "p-0 card card-tall");

  return (
    <div className={bodyClassList}>
      <div className="card-body p-2">
        <button onClick={() => setOpen(!open)} className="float-right btn btn-sm border border-magenta bg-transparent">{open ? "Show Less" : "Show More"}</button>
        <p className={headerClassList}>{title}</p>
        {open && (
          children
        )}
      </div>
    </div>
  )
}

export const CommissionStatementProcessingResult = (
  props: CommissionStatementProcessingResultProps
): React.JSX.Element => {
  const summary = props.data.summary;
  const inserted = props.data.inserted;
  const module = props.module;
  const mode = props.mode || 'plan';

  const insertedInds = inserted
    ? inserted.filter((mutation) => mutation.entity === 'ind')
    : [];

  const insertedIndPolicies = inserted
    ? inserted.filter((mutation) => mutation.entity === 'ind_policy')
    : [];

  const insertedIndCompHistories = inserted
    ? inserted.filter((mutation) => mutation.entity === 'ind_comp_history')
    : [];

  return (
    <>

      <ToggleCard className="mb-1" title={<>Profiles {mode === 'plan' ? <>to be Inserted</> : <>Inserted</>} <span className="badge badge-pill badge-magenta">{insertedInds.length}</span></>}>
        <p>
          The following profiles were found in the spreadsheet but didn't match with any profiles
          on your account.
        </p>
        <div className="alert small alert-info" role="alert">
          {insertedInds.length > 0 ? (
            insertedInds.map((mutation, key) => (
              <React.Fragment key={key}>
                {key !== 0 && <hr className="mt-2 mb-2" />}
                <p className="mb-0" dangerouslySetInnerHTML={{ __html: mutation.message }}></p>
              </React.Fragment>
            ))
          ) : (
            <p className="mb-0">No profile data needed to be inserted</p>
          )}
        </div>
      </ToggleCard>

      <ToggleCard className="mb-1" title={<>Policies {mode === 'plan' ? <>to be Inserted</> : <>Inserted</>} <span className="badge badge-pill badge-magenta">{insertedIndPolicies.length}</span></>}>
        <p>
          The following policies were found the the spreadsheet but didn't match with any policies
          on your account.
        </p>
        <div className="alert small alert-info" role="alert">
          {insertedIndPolicies.length > 0 ? (
            insertedIndPolicies.map((mutation, key) => (
              <React.Fragment key={key}>
                {key !== 0 && <hr className="mt-2 mb-2" />}
                <p className="mb-0" dangerouslySetInnerHTML={{ __html: mutation.message }}></p>
              </React.Fragment>
            ))
          ) : (
            <p className="mb-0">No profile data needed to be inserted</p>
          )}
        </div>
      </ToggleCard>

      <ToggleCard className="mb-1" title={<>Comp Histories {mode === 'plan' ? <>to be Inserted</> : <>Inserted</>} <span className="badge badge-pill badge-magenta">{insertedIndCompHistories.length}</span></>}>
        <p>The following comp history data will be inserted.</p>
        <div className="alert small alert-info" role="alert">
          {insertedIndCompHistories.length > 0 ? (
            insertedIndCompHistories.map((mutation, index) => (
              <React.Fragment key={index}>
                {index > 0 && <hr className="my-2" />}
                <p
                  className="mb-0"
                  dangerouslySetInnerHTML={{ __html: mutation.message }}
                />
              </React.Fragment>
            ))
          ) : (
            <p className="mb-0">No comp history data needed to be inserted.</p>
          )}
        </div>
      </ToggleCard>

      <ToggleCard title={<>Summary</>}>
        <p>Below is a summary of how the data in your sheet is cross referenced with the data we hold.</p>
        <table className="table border mt-3">
          <thead>
            <tr>
              <th style={{ width: '20%' }}>Agent</th>
              <th style={{ width: '20%' }}>Client</th>
              <th style={{ width: '20%' }}>Plan</th>
              <th style={{ width: '20%' }}>Policy</th>
              <th style={{ width: '20%' }}>Data</th>
            </tr>
          </thead>
          {summary &&
            summary.map((result, resultIndex) => {
              const agentData = result.agent;
              const indData = result.ind;
              const planData = result.plan;
              const policyData = result.policy;
              const compHistoryData = result.compHistory;

              const classList = [];

              if (result.policy.matched) {
              }

              const beneficiaryFullNameParts = Object.entries(indData.data).find((entry) => entry[0] === 'beneficiaryFullName');
              const beneficiaryFullName = beneficiaryFullNameParts?.[1];
              const beneficiaryMedicareIDarts = Object.entries(indData.data).find((entry) => entry[0] === 'beneficiaryMedicareID');
              const beneficiaryMedicareID = beneficiaryMedicareIDarts?.[1];

              return (<tbody key={result.row}>
                <tr className={classList.join(' ')}>
                  <td colSpan={5}>
                    <strong>{result.row + 1}. {beneficiaryFullName} {beneficiaryMedicareID && <>(MBI: {beneficiaryMedicareID})</>}</strong>
                    {result.policy.matched && result?.isDuplicate === false && (
                      <span className="badge small font-weight-normal text-white bg-success ml-1">
                        Data Inserted
                      </span>
                    )}
                    {result?.isDuplicate && (
                      <span className="badge small font-weight-normal text-white bg-warning ml-1">
                        Duplicate
                      </span>
                    )}
                  </td>
                </tr>
                <tr className={classList.join(' ')}>
                  <td valign="top">
                    <MatchBadge matched={agentData.matched} />
                    <p className="m-0 p-0">
                      <strong>Searched Using</strong>
                      <br />
                    </p>
                    {Object.entries(agentData.data).map(([itemKey, itemValue]) => (
                      <p key={itemKey} className="small m-0 p-0">
                        <strong>{itemKey}:</strong> {itemValue}
                      </p>
                    ))}
                  </td>
                  <td className="pb-3" valign="top">
                    {result.ind?.matchedMultiple ? (
                      <span className="badge small font-weight-normal text-white bg-warning">
                        Matched Multiple
                      </span>
                    ) : (
                      <MatchBadge matched={result.ind.matched} />
                    )}
                    <p className="m-0 p-0">
                      <strong>Searched Using</strong>
                      <br />
                    </p>
                    {Object.entries(indData.data).map(([itemKey, itemValue]) => (
                      <p key={itemKey} className="small m-0 p-0">
                        <strong>{itemKey}:</strong> {itemValue}
                      </p>
                    ))}
                  </td>
                  <td className="pb-3" valign="top">
                    <MatchBadge matched={result.plan.matched} />
                    <p className="m-0 p-0">
                      <strong>Searched Using</strong>
                      <br />
                    </p>
                    {Object.entries(planData.data).map(([itemKey, itemValue]) => (
                      <p key={itemKey} className="small m-0 p-0">
                        <strong>{itemKey}:</strong> {itemValue}
                      </p>
                    ))}
                  </td>
                  <td className="pb-3" valign="top">
                    <MatchBadge matched={result.policy.matched} />
                    <p className="m-0 p-0">
                      <strong>Searched Using</strong>
                      <br />
                    </p>
                    {Object.entries(policyData.data).map(([itemKey, itemValue]) => (
                      <p key={itemKey} className="small m-0 p-0">
                        <strong>{itemKey}:</strong> {itemValue}
                      </p>
                    ))}
                  </td>
                  <td className="pb-3" valign="top">
                    {compHistoryData && (
                      <>
                        <span className="badge small font-weight-normal text-white bg-success">
                          Data Inserted
                        </span>
                        <p className="m-0 p-0">
                          <strong>Details Updated</strong>
                          <br />
                        </p>
                        <p className="small m-0 p-0">
                          <strong>Phase:</strong> {compHistoryData.indCompHistoryPhase}
                        </p>
                        <p className="small m-0 p-0">
                          <strong>Amount:</strong> ${compHistoryData.indCompHistoryAmount}
                        </p>
                        <p className="small m-0 p-0">
                          <strong>Statement Date:</strong>{' '}
                          {compHistoryData.indCompHistoryStmtDate.date.split(' ')[0]}
                        </p>
                        <p className="small m-0 p-0">
                          <strong>Month Applied:</strong>{' '}
                          {compHistoryData.indCompHistoryMonthApplied.date.split(' ')[0].slice(0, 7)}
                        </p>
                      </>
                    )}
                  </td>
                </tr>
              </tbody>
              );
            })}
        </table>

      </ToggleCard>


    </>
  );
};
